import { Cradle, MusicalRelease, MusicalReleasesFilters, PaginatedEntity } from '@solo/app-core';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Flex, Text, Section, Img, RouterLink, ElementsPerPageSelector, Button } from '@/app/ui';
import { Modal, Icon } from '@/app/ui/molecules';
import { translate } from '@/app/i18n';
import Colors from '@/app/styles/Colors';
import WorksFilterComponent from '@/app/ui/organisms/Works/WorksListComponent/WorksFilterComponent/WorksFilterComponent';
import Pagination from '@/app/ui/organisms/Pagination/Pagination';
import { convertArrayToString } from '@/app/utils/ArrayToString';
import { useCradle } from '@/app/contexts';

interface MusicalReleaseListComponentPorps {
  musicalReleases: PaginatedEntity<MusicalRelease>;
  className?: string;
  filter: (filters: MusicalReleasesFilters) => void;
  jumpToPage: (page: number) => void;
  selectElementsPerPage: (nElements: number) => void;
  reloadReleases: () => void;
}
const releases = [10, 20, 30, 40, 50];
const MusicalReleasesListComponent = ({
  musicalReleases,
  className,
  filter,
  jumpToPage,
  selectElementsPerPage,
  reloadReleases,
}: MusicalReleaseListComponentPorps) => {
  const { musicalWorksBloc } = useCradle<Cradle>();
  const [artworkById, setArtworkById] = useState<Record<string, Blob> | undefined>();
  const [deletingMusicalRelease, setDeletingMusicalRelease] = useState<
    MusicalRelease | undefined
  >();

  const getArtworkUrl = (releaseId: string): string | undefined => {
    if (!artworkById) return undefined;

    return artworkById[releaseId]
      ? URL.createObjectURL(artworkById[releaseId])
      : '/placeholder-artwork.png';
  };

  const downloadArtworks = async () => {
    const artworks = await Promise.all(
      musicalReleases.data.map(async (musicalRelease) => {
        if (musicalRelease.resourceId) {
          const artwork = await musicalWorksBloc.getArtworkFile(musicalRelease.resourceId);
          return { [musicalRelease.id]: artwork };
        }
        return {};
      }),
    );
    const artworksObject = artworks.reduce((acc, curr) => ({ ...acc, ...curr }), {});
    setArtworkById((prev) => ({ ...prev, ...artworksObject }));
  };

  useEffect(() => {
    downloadArtworks();
  }, [musicalReleases]);

  const handleDeleteRelease = async (releaseId: string) => {
    try {
      await musicalWorksBloc.deleteMusicalRelease(releaseId);
      toast.success(translate('TOAST.MUSICAL_RELEASE_DELETED'));
      setDeletingMusicalRelease(undefined);
      reloadReleases();
    } catch (error) {
      toast.error(translate('TOAST.DELETE_ERROR'));
    }
  };

  return (
    <>
      <WorksFilterComponent filter={filter} />
      <ElementsPerPageSelector
        position="right"
        totalElements={musicalReleases.pagination.totalElements}
        options={releases}
        defaultElementsPerPage={releases[0]}
        onChange={(p) => selectElementsPerPage(p)}
      />
      <Section className={className}>
        <Flex.Container flex={1} className="table" flexDirection="column">
          <Flex.Container flex={1} className="thead">
            <Flex.Container flex={3} className="th ml-2" alignItems="center">
              <Text.Paragraph fontWeight={600} style={{ padding: 0 }}>
                {translate('WORKS.TITLE')}
              </Text.Paragraph>
            </Flex.Container>
            <Flex.Item flex={2} className="th">
              {translate('WORKS.ARTIST')}
            </Flex.Item>
            <Flex.Item flex={2} className="th">
              {translate('WORKS.TRACKS')}
            </Flex.Item>
            <Flex.Item flex={2} className="th">
              {translate('WORKS.STATUS')}
            </Flex.Item>
            <Flex.Item flex={2} className="th">
              {translate('WORKS.ACTIONS')}
            </Flex.Item>
          </Flex.Container>
          <Flex.Container className="tbody" flexDirection="column">
            {musicalReleases.data.map((musicalRelease) => (
              <Flex.Container key={musicalRelease.id} className="tr" alignItems="center">
                <Flex.Container className="td ml-2" flex={3} alignItems="center">
                  {getArtworkUrl(musicalRelease.id) ? (
                    <Img
                      src={getArtworkUrl(musicalRelease.id)}
                      height={40}
                      width={40}
                      style={{ marginRight: 15 }}
                    />
                  ) : (
                    <Icon.ImagePlaceholder style={{ marginRight: 15, width: 40, height: 40 }} />
                  )}
                  <RouterLink $transparent $variant="primary" to={`${musicalRelease.id}`}>
                    <Text.Paragraph fontWeight={600} color={Colors.primary}>
                      {musicalRelease.name}
                    </Text.Paragraph>
                  </RouterLink>
                </Flex.Container>
                <Flex.Item flex={2} className="td">
                  {convertArrayToString(
                    [musicalRelease.artistName].concat(
                      musicalRelease.contributors?.map((artist) => artist.artist.name),
                    ) || [musicalRelease.artistName],
                  )}
                </Flex.Item>
                <Flex.Item flex={2} className="td">
                  {musicalRelease.recordings.length}
                </Flex.Item>
                <Flex.Item flex={2} className="td">
                  {translate(`DIGITAL_DISTRIBUTION.ENUM_STATUS.${musicalRelease.status}`)}
                </Flex.Item>
                <Flex.Item flex={2} className="td">
                  <RouterLink $transparent $variant="primary" to={`${musicalRelease.id}`}>
                    <Icon.EditIcon width={18} style={{ cursor: 'pointer' }} />
                  </RouterLink>
                  <RouterLink $transparent $variant="primary" to={`${musicalRelease.id}/tracks`}>
                    <Icon.Music
                      style={{ cursor: 'pointer', marginLeft: 15, width: 20, height: 20 }}
                    />
                  </RouterLink>
                  <Icon.BinIcon
                    width={16}
                    style={{ cursor: 'pointer', marginLeft: 15 }}
                    onClick={() => setDeletingMusicalRelease(musicalRelease)}
                  />
                </Flex.Item>
              </Flex.Container>
            ))}
          </Flex.Container>
        </Flex.Container>
      </Section>
      <Pagination
        totalElements={musicalReleases.pagination.totalElements}
        pagination={musicalReleases.pagination}
        jumpToPage={jumpToPage}
        maxDisplayedPages={8}
      />
      <Modal
        isOpen={deletingMusicalRelease !== undefined}
        onClose={() => setDeletingMusicalRelease(undefined)}
        closeable
        position="center"
        size="sm"
      >
        <Flex.Container flexDirection="column" gap="40px" padding="10px 20px">
          <Text.LabelMedium>
            {translate('TRACKS.SURE_DELETE')} {deletingMusicalRelease?.name} ?
          </Text.LabelMedium>
          <Flex.Container style={{ marginLeft: 'auto' }} gap="20px">
            <Button
              $transparent
              $variant="primary"
              onClick={() => {
                setDeletingMusicalRelease(undefined);
              }}
            >
              <Text.SectionSubtitle>{translate('PROFILE_INFO.CANCEL')}</Text.SectionSubtitle>
            </Button>

            <Button
              $size="md"
              $variant="primary"
              style={{ width: '290px' }}
              onClick={() => {
                if (!deletingMusicalRelease) return;

                handleDeleteRelease(deletingMusicalRelease.id);
              }}
            >
              <Text.SectionSubtitle style={{ padding: 0 }} fontWeight={600}>
                {translate('BUTTON.DELETE')}
              </Text.SectionSubtitle>
            </Button>
          </Flex.Container>
        </Flex.Container>
      </Modal>
    </>
  );
};

// export default WorksListComponent;
export default styled(MusicalReleasesListComponent)`
  .box {
    padding: 2px 0px;
    width: fit-content;
    color: ${Colors.white};
    width: 80px;
    text-align: center;
  }
`;
